import { useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';
import RewriteModal from '../components/RewriteModal';

type Step = 'input' | 'questions' | 'score';

export default function LLMVisibilityPage() {
  const [step, setStep] = useState<Step>('input');
  const [productCategory, setProductCategory] = useState('');
  const [brandName, setBrandName] = useState('');
  const [questionsOutput, setQuestionsOutput] = useState('');
  const [editedQuestions, setEditedQuestions] = useState('');
  const [scoreOutput, setScoreOutput] = useState('');
  const [editedScore, setEditedScore] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState('');
  const [tokens, setTokens] = useState({ input: 0, output: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [activeTextarea, setActiveTextarea] = useState<'questions' | 'score'>('questions');

  const handleTextSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>, field: 'questions' | 'score') => {
    const ta = e.currentTarget;
    const selected = ta.value.substring(ta.selectionStart, ta.selectionEnd);
    setSelectedText(selected);
    setActiveTextarea(field);
  };

  const handleRewriteAccept = (rewrittenText: string) => {
    if (activeTextarea === 'questions') {
      setEditedQuestions(editedQuestions.replace(selectedText, rewrittenText));
    } else {
      setEditedScore(editedScore.replace(selectedText, rewrittenText));
    }
    setShowRewriteModal(false);
    setSelectedText('');
  };

  const handleGenerateQuestions = async () => {
    if (!productCategory.trim()) {
      setError('Please enter a product category');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.post('/llmvisibility/generate', {
        product_category: productCategory.trim(),
      });
      setQuestionsOutput(res.data.questions_output);
      setEditedQuestions(res.data.questions_output);
      setTokens({ input: res.data.input_tokens, output: res.data.output_tokens });
      setStep('questions');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateScore = async () => {
    if (!brandName.trim()) {
      setError('Please enter your brand name');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.post('/llmvisibility/score', {
        product_category: productCategory.trim(),
        questions_output: questionsOutput,
        brand_name: brandName.trim(),
      });
      setScoreOutput(res.data.score_output);
      setEditedScore(res.data.score_output);
      setTokens(prev => ({
        input: prev.input + res.data.input_tokens,
        output: prev.output + res.data.output_tokens,
      }));
      setStep('score');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Score calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep('input');
    setProductCategory('');
    setBrandName('');
    setQuestionsOutput('');
    setEditedQuestions('');
    setScoreOutput('');
    setEditedScore('');
    setError(null);
    setTokens({ input: 0, output: 0 });
  };

  const handleCopy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const CopyButton = ({ text, label }: { text: string; label: string }) => (
    <button
      onClick={() => handleCopy(text, label)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
    >
      {copied === label ? (
        <>
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-green-600">Copied!</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy
        </>
      )}
    </button>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">LLM Visibility Calculator</h1>
          <p className="text-gray-500 mt-1">
            Measure your brand's visibility in AI-generated answers. Generate category questions and calculate your brand's approximate LLM visibility score.
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-3 mb-6">
          {[
            { key: 'input', label: '1. Category', num: 1 },
            { key: 'questions', label: '2. Questions', num: 2 },
            { key: 'score', label: '3. Score', num: 3 },
          ].map((s, i) => {
            const isActive = s.key === step;
            const isDone = (s.key === 'input' && step !== 'input') || (s.key === 'questions' && step === 'score');
            return (
              <div key={s.key} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-0.5 ${isDone || isActive ? 'bg-brand-500' : 'bg-gray-200'}`} />}
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  isActive ? 'bg-brand-100 text-brand-700' : isDone ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {isDone ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-current/10 flex items-center justify-center text-xs">{s.num}</span>
                  )}
                  {s.label}
                </div>
              </div>
            );
          })}
          {step !== 'input' && (
            <button onClick={handleReset} className="ml-auto text-sm text-gray-500 hover:text-gray-700">
              Start Over
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Step 1: Product Category Input */}
        {step === 'input' && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Category</label>
                <input
                  type="text"
                  value={productCategory}
                  onChange={(e) => setProductCategory(e.target.value)}
                  placeholder="e.g., small business accounting software, side sleeper mattresses, enterprise CRM..."
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  onKeyDown={(e) => e.key === 'Enter' && !loading && handleGenerateQuestions()}
                />
                <p className="mt-1.5 text-xs text-gray-400">Be specific — "small business accounting software" works better than just "software"</p>
              </div>
              <button
                onClick={handleGenerateQuestions}
                disabled={loading || !productCategory.trim()}
                className="w-full py-3 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating 20 questions...
                  </>
                ) : (
                  'Generate Questions'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Questions Output + Brand Input */}
        {step === 'questions' && (
          <>
            {/* Questions Result */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                <div>
                  <h3 className="font-medium text-gray-900">20 Brand-Driven Q&As: {productCategory}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tokens: {tokens.input.toLocaleString()} in / {tokens.output.toLocaleString()} out
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRewriteModal(true)}
                    disabled={!selectedText || activeTextarea !== 'questions'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Rewrite
                  </button>
                  <CopyButton text={editedQuestions} label="questions" />
                </div>
              </div>
              <div className="p-6">
                <textarea
                  value={editedQuestions}
                  onChange={(e) => setEditedQuestions(e.target.value)}
                  onSelect={(e) => handleTextSelect(e, 'questions')}
                  className="w-full min-h-[500px] rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Brand Name Input */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Brand Name</label>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="e.g., QuickBooks, Purple, HubSpot..."
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    onKeyDown={(e) => e.key === 'Enter' && !loading && handleCalculateScore()}
                  />
                  <p className="mt-1.5 text-xs text-gray-400">Enter your brand to calculate its approximate LLM visibility score</p>
                </div>
                <button
                  onClick={handleCalculateScore}
                  disabled={loading || !brandName.trim()}
                  className="w-full py-3 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Calculating visibility score...
                    </>
                  ) : (
                    'Calculate Visibility Score'
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Score Output */}
        {step === 'score' && (
          <>
            {/* Score Result */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                <div>
                  <h3 className="font-medium text-gray-900">Visibility Score: {brandName}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Category: {productCategory} | Total tokens: {tokens.input.toLocaleString()} in / {tokens.output.toLocaleString()} out
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowRewriteModal(true)}
                    disabled={!selectedText || activeTextarea !== 'score'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Rewrite
                  </button>
                  <CopyButton text={editedScore} label="score" />
                </div>
              </div>
              <div className="p-6">
                <textarea
                  value={editedScore}
                  onChange={(e) => setEditedScore(e.target.value)}
                  onSelect={(e) => handleTextSelect(e, 'score')}
                  className="w-full min-h-[400px] rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Questions (collapsed) */}
            <details className="bg-white rounded-xl shadow-sm border border-gray-200">
              <summary className="flex items-center justify-between px-6 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                <h3 className="font-medium text-gray-900">View Generated Questions</h3>
                <CopyButton text={editedQuestions} label="questions-collapsed" />
              </summary>
              <div className="p-6 border-t border-gray-200">
                <textarea
                  value={editedQuestions}
                  onChange={(e) => setEditedQuestions(e.target.value)}
                  onSelect={(e) => handleTextSelect(e, 'questions')}
                  className="w-full min-h-[400px] rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                  spellCheck={false}
                />
              </div>
            </details>
          </>
        )}
      </div>

      <RewriteModal
        isOpen={showRewriteModal}
        selectedText={selectedText}
        onClose={() => { setShowRewriteModal(false); setSelectedText(''); }}
        onAccept={handleRewriteAccept}
      />
    </Layout>
  );
}
